import React from "react";
import { Typography, Grid, Stack, Box } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { AIButton } from "../../../../components";
import { useRecoilValue } from "recoil";
import organizationIdSelector from "../../../../atoms/organizationIdSelector";
import MealPlanDataGrid from "./MealPlanDataGrid";
import MealPlanGeneratorDialog from "./MealPlanGeneratorDialog";
import useRecipes from "../../../../hooks/useRecipes";
import useEvents from "../../../../hooks/useEvents";
import { suggestCategory } from "../../../../data/ShoppingCategories";
import { nanoid } from "nanoid";
import EmptyMealPlans from "./EmptyMealPlans";
import useEntitlements from "../../../../hooks/useEntitlements";
import UpgradePrompt from "../../../../components/organisms/UpgradePrompt";

const EditMealPlans = ({ onShowDetails }) => {
  const confirm = useConfirm();
  const [, setSelectedMealPlan] = React.useState(null);
  const [showMealPlanGenerator, setShowMealPlanGenerator] = React.useState(false);
  const [editingMealPlan, setEditingMealPlan] = React.useState(null); // For editing existing meal plans with generator
  const events = useEvents();
  const organizationId = useRecoilValue(organizationIdSelector);
  const { hasFeature, organizationData } = useEntitlements();
  const canUseMealPlanAi = hasFeature("meal_plan_data_entry_assistance");
  const { mealPlans, mealPlansLoading, archiveMealPlan, editMealPlan, recipes } = useRecipes({
    organizationId,
  });

  // Function to add ingredient to the database
  const addIngredientByObject = async (organizationId, ingredient) => {
    const firebase = (await import("firebase/compat/app")).default;
    const id = nanoid();
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("ingredients")
      .doc(id);

    const ingredientData = {
      ...ingredient,
      id,
    };

    await ref.set(ingredientData, { merge: true });
    return ingredientData;
  };

  // Function to create meal plan from generated data with proper transaction handling
  const createMealPlanFromGenerated = async (transformedData) => {
    const { consola } = await import("consola");

    try {
      // Validate transformed data structure
      if (!transformedData || !transformedData.mealPlanMetadata) {
        throw new Error("Invalid meal plan data structure");
      }

      const { newRecipes, selectedRecipes, mealPlanMetadata } = transformedData;

      consola.debug("Starting meal plan creation transaction...");
      consola.debug("New recipes to create:", newRecipes?.length || 0);
      consola.debug(
        "Selected recipes references:",
        selectedRecipes?.length || 0,
      );

      const finalSelectedRecipes = [...(selectedRecipes || [])];

      // Step 1: If there are new recipes to create
      if (newRecipes && newRecipes.length > 0) {
        // Extract and deduplicate ingredients from new recipes
        const uniqueIngredients = extractUniqueIngredients(newRecipes);
        consola.debug(
          "Unique ingredients to process:",
          uniqueIngredients.length,
        );

        // Step 2: Check for existing ingredients and create new ones
        const ingredientIdMap = await processIngredients(
          organizationId,
          uniqueIngredients,
        );

        // Step 3: Create new recipes with ingredient references
        const recipeIdMap = await processNewRecipes(
          organizationId,
          newRecipes,
          ingredientIdMap,
        );

        console.log("Recipe ID mapping after creation:", recipeIdMap);

        // Step 4: Update selectedRecipes with actual Firebase recipe IDs
        finalSelectedRecipes.forEach((selectedRecipe) => {
          if (recipeIdMap.has(selectedRecipe.recipeId)) {
            console.log(
              `Updating recipe ID from ${selectedRecipe.recipeId} to ${recipeIdMap.get(selectedRecipe.recipeId)}`,
            );
            selectedRecipe.recipeId = recipeIdMap.get(selectedRecipe.recipeId);
          }
        });
      }

      // Step 5: Create the meal plan document
      const mealPlanId = await createMealPlanDocument(
        organizationId,
        mealPlanMetadata,
        finalSelectedRecipes,
      );

      consola.success("Successfully created meal plan:", mealPlanId);

      // Log the success
      events.logButtonClick({
        buttonName: "Generate Meal Plan - Success",
        pageName: "Nutrition",
        tabName: "Meal Plans",
      });

      return mealPlanId;
    } catch (error) {
      consola.error("Error creating meal plan:", error);
      throw error;
    }
  };

  // Helper function to extract unique ingredients from recipes
  const extractUniqueIngredients = (recipes) => {
    const ingredientMap = new Map();

    // Handle case where recipes is undefined, null, or empty
    if (!recipes || !Array.isArray(recipes) || recipes.length === 0) {
      return [];
    }

    recipes.forEach((recipe) => {
      // Handle both original AI format (ing) and transformed format (ingredients)
      const ingredients = recipe.ingredients || recipe.ing || [];

      if (Array.isArray(ingredients)) {
        ingredients.forEach((ingredient) => {
          // Handle both AI format and transformed format
          const ingredientData = {
            name: (ingredient.name || ingredient.n || "").trim(),
            unit: ingredient.unit || ingredient.u || "g",
            category:
              ingredient.category ||
              suggestCategory(ingredient.name || ingredient.n || ""),
            proteins: ingredient.proteins || ingredient.mac?.p || 0,
            carbs: ingredient.carbs || ingredient.mac?.c || 0,
            fats: ingredient.fats || ingredient.mac?.f || 0,
          };

          if (ingredientData.name) {
            const key = `${ingredientData.name.toLowerCase()}_${ingredientData.unit}`;
            if (!ingredientMap.has(key)) {
              ingredientMap.set(key, ingredientData);
            }
          }
        });
      }
    });

    return Array.from(ingredientMap.values());
  };

  // Process ingredients: check for existing and create new ones
  const processIngredients = async (orgId, ingredients) => {
    const firebase = (await import("firebase/compat/app")).default;
    const ingredientIdMap = new Map();

    // Handle case where ingredients is undefined, null, or empty
    if (
      !ingredients ||
      !Array.isArray(ingredients) ||
      ingredients.length === 0
    ) {
      console.log("No ingredients to process");
      return ingredientIdMap;
    }

    // Check for existing ingredients first
    for (const ingredient of ingredients) {
      const existingIngredientQuery = await firebase
        .firestore()
        .collection("organizations")
        .doc(orgId)
        .collection("ingredients")
        .where("name", "==", ingredient.name)
        .where("unit", "==", ingredient.unit)
        .limit(1)
        .get();

      if (!existingIngredientQuery.empty) {
        // Use existing ingredient
        const existingIngredient = existingIngredientQuery.docs[0];
        const key = `${ingredient.name.toLowerCase().trim()}_${ingredient.unit}`;
        ingredientIdMap.set(key, existingIngredient.id);
        console.log(`Using existing ingredient: ${ingredient.name}`);
      } else {
        // Create new ingredient
        const ingredientData = await addIngredientByObject(orgId, ingredient);
        const key = `${ingredient.name.toLowerCase().trim()}_${ingredient.unit}`;
        ingredientIdMap.set(key, ingredientData.id);
        console.log(`Created new ingredient: ${ingredient.name}`);
      }
    }

    return ingredientIdMap;
  };

  // Process new recipes with ingredient references
  const processNewRecipes = async (orgId, newRecipes, ingredientIdMap) => {
    const firebase = (await import("firebase/compat/app")).default;
    const createdRecipeMap = new Map();

    // Handle case where newRecipes is undefined, null, or empty
    if (!newRecipes || !Array.isArray(newRecipes) || newRecipes.length === 0) {
      console.log("No new recipes to process");
      return createdRecipeMap;
    }

    for (const recipe of newRecipes) {
      console.log("Processing new recipe for Firebase save:", {
        name: recipe.name || recipe.n,
        hasDescription: !!(recipe.description || recipe.d),
        hasInstructions: !!(recipe.instructions || recipe.i),
        hasIngredients: !!(recipe.ingredients || recipe.ing),
        description: recipe.description || recipe.d,
        instructions: recipe.instructions || recipe.i,
      });

      const recipeId = nanoid();
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(orgId)
        .collection("recipes")
        .doc(recipeId);

      // Convert AI recipe to Firebase format with ingredient references
      const recipeData = {
        id: recipeId,
        name: recipe.name || recipe.n,
        description: recipe.description || recipe.d || "",
        instructions:
          recipe.instructions ||
          (Array.isArray(recipe.i)
            ? recipe.i
                .map((instruction) => instruction.step || instruction)
                .join(". ")
            : ""),
        servings: recipe.servings || recipe.ns || 1,
        mealType: recipe.mealType,
        kCals: recipe.macros?.kCals || recipe.mac?.kCals || 0,
        proteins: recipe.macros?.p || recipe.mac?.p || 0,
        carbs: recipe.macros?.c || recipe.mac?.c || 0,
        fats: recipe.macros?.f || recipe.mac?.f || 0,
        fiber:
          recipe.fiber ??
          recipe.macros?.fi ??
          recipe.mac?.fi ??
          recipe.mac?.fiber ??
          0,
        ingredients: (recipe.ingredients || recipe.ing || []).map(
          (ingredient) => {
            // Handle both transformed format and original AI format
            const ingredientData = {
              name: ingredient.name || ingredient.n,
              amount: ingredient.amount || ingredient.q || 1,
              unit: ingredient.unit || ingredient.u || "g",
              proteins: ingredient.proteins || ingredient.mac?.p || 0,
              carbs: ingredient.carbs || ingredient.mac?.c || 0,
              fats: ingredient.fats || ingredient.mac?.f || 0,
              fiber: ingredient.fiber || ingredient.mac?.fi || 0,
            };

            const key = `${ingredientData.name.toLowerCase().trim()}_${ingredientData.unit}`;
            return {
              id: nanoid(),
              ingredientId: ingredientIdMap.get(key),
              ...ingredientData,
              createdOn: new Date().toISOString(),
              updatedOn: new Date().toISOString(),
            };
          },
        ),
        isPublic: false,
        isArchived: false,
        createdOn: new Date().toISOString(),
        updatedOn: new Date().toISOString(),
      };

      await ref.set(recipeData, { merge: true });

      // Map temporary recipe ID to actual Firebase recipe ID
      createdRecipeMap.set(recipe.id, recipeId);

      console.log(
        `Created recipe: ${recipe.name} (${recipeId}) - mapped from temp ID: ${recipe.id}`,
      );
    }

    return createdRecipeMap;
  };

  // Helper function to calculate actual totals from selectedRecipes when not provided
  const calculateActualTotals = async (orgId, selectedRecipes) => {
    if (!selectedRecipes || selectedRecipes.length === 0) {
      return {
        totalkCals: 0,
        totalProteins: 0,
        totalCarbs: 0,
        totalFats: 0,
        totalFiber: 0,
        daysWithRecipes: 0,
      };
    }

    const firebase = (await import("firebase/compat/app")).default;
    const dayNames = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    let totalDays = 0;
    let sumKCals = 0,
      sumProteins = 0,
      sumCarbs = 0,
      sumFats = 0,
      sumFiber = 0;

    // Group recipes by day to calculate daily totals
    const dailyTotals = {};
    dayNames.forEach((day) => {
      dailyTotals[day] = {
        kCals: 0,
        proteins: 0,
        carbs: 0,
        fats: 0,
        fiber: 0,
        hasRecipes: false,
      };
    });

    // Process each selected recipe
    for (const selectedRecipe of selectedRecipes) {
      try {
        // Get the actual recipe data from Firebase
        const recipeDoc = await firebase
          .firestore()
          .collection("organizations")
          .doc(orgId)
          .collection("recipes")
          .doc(selectedRecipe.recipeId)
          .get();

        if (recipeDoc.exists) {
          const recipe = recipeDoc.data();
          const servingsPerDay = selectedRecipe.servingsPerDay || 1;

          // Calculate per-serving macros
          const perServingKCals =
            (parseFloat(recipe.kCals) || 0) / (recipe.servings || 1);
          const perServingProteins =
            (parseFloat(recipe.proteins) || 0) / (recipe.servings || 1);
          const perServingCarbs =
            (parseFloat(recipe.carbs) || 0) / (recipe.servings || 1);
          const perServingFats =
            (parseFloat(recipe.fats) || 0) / (recipe.servings || 1);
          const perServingFiber =
            (parseFloat(recipe.fiber) || 0) / (recipe.servings || 1);

          // Add to each assigned day
          const assignedDays = selectedRecipe.days.includes("all")
            ? dayNames
            : selectedRecipe.days;
          assignedDays.forEach((day) => {
            if (dayNames.includes(day.toLowerCase())) {
              const dayKey = day.toLowerCase();
              dailyTotals[dayKey].kCals += perServingKCals * servingsPerDay;
              dailyTotals[dayKey].proteins +=
                perServingProteins * servingsPerDay;
              dailyTotals[dayKey].carbs += perServingCarbs * servingsPerDay;
              dailyTotals[dayKey].fats += perServingFats * servingsPerDay;
              dailyTotals[dayKey].fiber += perServingFiber * servingsPerDay;
              dailyTotals[dayKey].hasRecipes = true;
            }
          });
        }
      } catch (error) {
        console.warn(
          `Error processing recipe ${selectedRecipe.recipeId}:`,
          error,
        );
      }
    }

    // Calculate averages from days with recipes
    dayNames.forEach((day) => {
      const dayTotal = dailyTotals[day];
      if (dayTotal.hasRecipes) {
        totalDays++;
        sumKCals += dayTotal.kCals;
        sumProteins += dayTotal.proteins;
        sumCarbs += dayTotal.carbs;
        sumFats += dayTotal.fats;
        sumFiber += dayTotal.fiber;
      }
    });

    // Return averages if we have data, otherwise return zeros
    if (totalDays > 0) {
      return {
        totalkCals: Math.round(sumKCals / totalDays),
        totalProteins: Math.round(sumProteins / totalDays),
        totalCarbs: Math.round(sumCarbs / totalDays),
        totalFats: Math.round(sumFats / totalDays),
        totalFiber: Math.round(sumFiber / totalDays),
        daysWithRecipes: totalDays,
      };
    }

    return {
      totalkCals: 0,
      totalProteins: 0,
      totalCarbs: 0,
      totalFats: 0,
      totalFiber: 0,
      daysWithRecipes: 0,
    };
  };

  // Create meal plan document
  const createMealPlanDocument = async (orgId, metadata, recipeReferences) => {
    const firebase = (await import("firebase/compat/app")).default;
    const mealPlanId = nanoid();

    const mealPlanRef = firebase
      .firestore()
      .collection("organizations")
      .doc(orgId)
      .collection("mealPlans")
      .doc(mealPlanId);

    // Use calculated totals from metadata if available, otherwise calculate from recipes
    let totals = metadata.totals;
    if (!totals) {
      console.log("No totals in metadata, calculating from recipes...");
      totals = await calculateActualTotals(orgId, recipeReferences);
    }

    console.log("Creating meal plan with totals:", totals);

    const mealPlanData = {
      id: mealPlanId,
      name: metadata.name,
      description: metadata.description || "",
      selectedRecipes: recipeReferences,
      totals,
      macroTargets: metadata.macroTargets,
      calculatorSettings: metadata.calculatorSettings,
      dietaryRestrictions: metadata.dietaryRestrictions,
      customInstructions: metadata.customInstructions,
      createdOn: new Date().toISOString(),
      updatedOn: new Date().toISOString(),
      isArchived: false,
    };

    await mealPlanRef.set(mealPlanData, { merge: true });
    return mealPlanId;
  };

  // Function to update existing meal plan from generator
  const updateMealPlanFromGenerated = async (transformedData) => {
    const { consola } = await import("consola");

    try {
      console.log("updateMealPlanFromGenerated called with:", transformedData);

      // Validate transformed data structure
      if (
        !transformedData ||
        !transformedData.mealPlanMetadata ||
        !transformedData.isEditMode
      ) {
        console.error("Invalid data structure:", {
          hasData: !!transformedData,
          hasMetadata: !!transformedData?.mealPlanMetadata,
          hasEditMode: !!transformedData?.isEditMode,
        });
        throw new Error("Invalid meal plan update data structure");
      }

      const { newRecipes, selectedRecipes, mealPlanMetadata } = transformedData;
      console.log("Processing update with:", {
        newRecipesCount: newRecipes?.length || 0,
        selectedRecipesCount: selectedRecipes?.length || 0,
        mealPlanId: mealPlanMetadata.id,
      });

      const firebase = (await import("firebase/compat/app")).default;

      // Start a transaction for atomic updates
      return firebase.firestore().runTransaction(async (transaction) => {
        consola.log("Starting meal plan update transaction...");

        // Handle new recipes if any were added during editing
        if (newRecipes && newRecipes.length > 0) {
          // Extract and process ingredients from new recipes
          const uniqueIngredients = extractUniqueIngredients(newRecipes);
          const ingredientIdMap = await processIngredients(
            organizationId,
            uniqueIngredients,
          );

          // Process new recipes and get their IDs
          const recipeIdMap = await processNewRecipes(
            organizationId,
            newRecipes,
            ingredientIdMap,
          );

          console.log("Recipe ID mapping after creation:", recipeIdMap);

          // Update recipe IDs in selectedRecipes array
          selectedRecipes.forEach((selectedRecipe) => {
            // Check if this selectedRecipe references a newly created recipe
            if (recipeIdMap.has(selectedRecipe.recipeId)) {
              console.log(
                `Updating recipe ID from ${selectedRecipe.recipeId} to ${recipeIdMap.get(selectedRecipe.recipeId)}`,
              );
              selectedRecipe.recipeId = recipeIdMap.get(
                selectedRecipe.recipeId,
              );
            }
          });
        }

        // Update the meal plan document with the new selectedRecipes array
        const mealPlanRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("mealPlans")
          .doc(mealPlanMetadata.id);

        // Always recalculate totals when updating a meal plan since recipes may have changed
        console.log("Recalculating totals for updated meal plan...");
        const totals = await calculateActualTotals(
          organizationId,
          selectedRecipes,
        );

        console.log("Updating meal plan with totals:", totals);

        const updateData = {
          ...mealPlanMetadata,
          selectedRecipes: selectedRecipes,
          totals,
          updatedOn: new Date().toISOString(),
        };

        console.log("About to save meal plan update:", updateData);
        transaction.update(mealPlanRef, updateData);

        consola.success(
          `Successfully updated meal plan: ${mealPlanMetadata.name}`,
        );
        return mealPlanMetadata.id;
      });
    } catch (error) {
      consola.error("Error updating meal plan:", error);
      throw error;
    }
  };

  return (
    <>
      {/* Show loading first, then empty state when data confirms no meal plans */}
      {mealPlansLoading ? (
        <Typography>Loading meal plans...</Typography>
      ) : !mealPlans || mealPlans.length === 0 ? (
        <EmptyMealPlans
          onGenerateMealPlan={
            canUseMealPlanAi
              ? () => {
                  setSelectedMealPlan(null);
                  setShowMealPlanGenerator(true);
                }
              : null
          }
          upgradePrompt={
            !canUseMealPlanAi ? (
              <UpgradePrompt
                organizationData={organizationData}
                minTier="pro"
                title="Upgrade to generate meal plans with AI"
                description="AI meal plan generation is available on Pro."
                featureName="meal_plan_data_entry_assistance"
              />
            ) : null
          }
        />
      ) : (
        <>
          <Grid container style={{ marginTop: 0 }}>
            <Grid item xs={8}>
              <Typography variant="h6">Meal Plans</Typography>
              <Typography
                style={{ fontSize: 16, fontWeight: "normal", marginBottom: 12 }}
              >
                Create meal plans utilizing the recipes stored in your recipes
                database.
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Stack direction={"row"} spacing={1} justifyContent={"flex-end"}>
                {canUseMealPlanAi ? (
                  <AIButton
                    sx={{ marginTop: 12 }}
                    onClick={() => {
                      setSelectedMealPlan(null);
                      setShowMealPlanGenerator(true);
                      events.logButtonClick({
                        buttonName: "Generate Meal Plan",
                        pageName: "Nutrition",
                        tabName: "Meal Plans",
                      });
                    }}
                  >
                    Generate Meal Plan
                  </AIButton>
                ) : (
                  <Box sx={{ mt: 1.5, maxWidth: 360 }}>
                    <UpgradePrompt
                      organizationData={organizationData}
                      minTier="pro"
                      variant="banner"
                      title="Upgrade for AI meal plans"
                      description="AI meal plan generation is available on Pro."
                      featureName="meal_plan_data_entry_assistance"
                    />
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>

          <MealPlanDataGrid
            mealPlans={mealPlans}
            onClick={(mealPlan) => {
              setSelectedMealPlan(mealPlan);
              if (onShowDetails) {
                onShowDetails(mealPlan);
              }
              // setMode("details-meal-plan");
            }}
            onEdit={(mealPlan) => {
              setEditingMealPlan(mealPlan);
              setSelectedMealPlan(mealPlan);
              events.logButtonClick({
                buttonName: "Edit Meal Plan",
                pageName: "Nutrition",
                tabName: "Meal Plans",
              });
            }}
            onDelete={(mealPlan) => {
              confirm({
                title: "Archive this meal plan?",
                description: "This will archive the meal plan. Continue?",
              })
                .then(async () => {
                  await archiveMealPlan(mealPlan.id);
                  setSelectedMealPlan(null);
                  events.logButtonClick({
                    buttonName: "Delete Meal Plan",
                    pageName: "Nutrition",
                    tabName: "Meal Plans",
                  });
                })
                .catch(() => {
                  // User cancelled the confirmation - no action needed
                });
            }}
            onUpdate={editMealPlan}
          />
        </>
      )}

      {/* Dialogs - Always rendered regardless of empty state */}

      {showMealPlanGenerator && (
        <MealPlanGeneratorDialog
          onDialogClosed={async (result) => {
            if (result) {
              try {
                await createMealPlanFromGenerated(result);
                // Close dialog only after successful save
                setShowMealPlanGenerator(false);
                // Optionally show success message or refresh data
              } catch (error) {
                console.error("Failed to create meal plan:", error);
                // Close dialog even on error but could show error message to user
                setShowMealPlanGenerator(false);
              }
            } else {
              // User cancelled or there was an error, close dialog
              setShowMealPlanGenerator(false);
            }
          }}
        />
      )}

      {editingMealPlan && (
        <MealPlanGeneratorDialog
          existingMealPlan={editingMealPlan}
          onDialogClosed={async (result) => {
            if (result) {
              try {
                if (result.isEditMode) {
                  await updateMealPlanFromGenerated(result);
                } else {
                  await createMealPlanFromGenerated(result);
                }
                // Close dialog only after successful save
                setEditingMealPlan(null);
                setSelectedMealPlan(null);
                // Optionally show success message or refresh data
              } catch (error) {
                console.error("Failed to update meal plan:", error);
                // Close dialog even on error but could show error message to user
                setEditingMealPlan(null);
                setSelectedMealPlan(null);
              }
            } else {
              // User cancelled or there was an error, close dialog
              setEditingMealPlan(null);
              setSelectedMealPlan(null);
            }
          }}
        />
      )}
    </>
  );
};

export default EditMealPlans;
